/**
 * The catch-up flow (plan I2; spec §13, §14, D-099): after a load or a long hidden tab, the Reach runs the time
 * the player was away. `elapsed` is capped at strategic.json `catchUp.capS` (8 h); the overlay reads
 * "Catching up the Reach… day n of N" from the host's progress callback; the away report goes out on the bus
 * (`awayReport`, drawn by I6) only when the player was away at least `reportMinS`: a reload a few seconds later
 * still catches up but raises no empty modal over the station (D-TBD, C21). The caller then resumes (docked, after
 * a load: M2's `enterCareer`).
 * One flow runs at a time: a second request while one runs joins it.
 */
import { logger } from '../core/logger'
import type { AwayReport } from '../strategic/types'

/** The client calls the flow needs. */
export interface CatchUpClient {
  catchUp(seconds: number, onProgress?: (day: number, days: number) => void): Promise<AwayReport>
}

/** The progress overlay (a tiny DOM panel in the browser, a fake in tests). */
export interface CatchUpOverlay {
  show(text: string): void
  hide(): void
}

export interface CatchUpFlowOptions {
  client: CatchUpClient
  /** The offline cap in seconds (strategic.json `catchUp.capS`). */
  capS: number
  overlay: CatchUpOverlay
  /** Receives the report (the game bus `awayReport` event) when the absence was at least `reportMinS`. */
  report: (report: AwayReport) => void
  /** The shortest absence (seconds) that shows the away report (strategic.json `catchUp.hiddenResumeS`); default 0. */
  reportMinS?: number
}

export interface CatchUpFlow {
  /** Catches up `elapsedSeconds` (capped); resolves with the report, or null when nothing was away. */
  run(elapsedSeconds: number): Promise<AwayReport | null>
  readonly running: boolean
}

/** The overlay line for day `day` of `days` (1-based for the reader). */
export function catchUpText(day: number, days: number): string {
  const total = Math.max(1, Math.ceil(days))
  const n = Math.min(total, Math.max(1, Math.floor(day) + 1))
  return `Catching up the Reach… day ${n} of ${total}`
}

export function createCatchUpFlow(opts: CatchUpFlowOptions): CatchUpFlow {
  let inFlight: Promise<AwayReport | null> | null = null

  async function go(seconds: number): Promise<AwayReport | null> {
    opts.overlay.show(catchUpText(0, 1))
    try {
      const report = await opts.client.catchUp(seconds, (day, days) => opts.overlay.show(catchUpText(day, days)))
      if (seconds >= (opts.reportMinS ?? 0)) opts.report(report)
      return report
    } finally {
      opts.overlay.hide()
    }
  }

  return {
    run(elapsedSeconds: number): Promise<AwayReport | null> {
      if (inFlight !== null) return inFlight
      const seconds = Number.isFinite(elapsedSeconds) ? Math.min(Math.max(0, elapsedSeconds), opts.capS) : 0
      if (seconds <= 0) return Promise.resolve(null)
      inFlight = go(seconds).finally(() => {
        inFlight = null
      })
      return inFlight
    },
    get running(): boolean {
      return inFlight !== null
    },
  }
}

/** A minimal DOM overlay for the flow: one centred line in `root` (the `#overlay` layer). */
export function createDomCatchUpOverlay(root: HTMLElement): CatchUpOverlay {
  let el: HTMLElement | null = null
  return {
    show(text: string): void {
      if (el === null) {
        el = root.ownerDocument.createElement('div')
        el.className = 'strategic-catchup'
        el.setAttribute('role', 'status')
        el.style.cssText = 'position:fixed;inset:auto 0 45% 0;text-align:center;font:600 16px system-ui;color:#cfe;pointer-events:none;z-index:50'
        root.appendChild(el)
      }
      el.textContent = text
    },
    hide(): void {
      el?.remove()
      el = null
    },
  }
}

/**
 * The overlay the link uses (I6): the plain `fallback` line until `load` (a dynamic import of I6's styled
 * overlay, so React stays out of this chunk) resolves, then the styled one. The load starts on the first `show`;
 * if it lands mid catch-up, the styled overlay takes over with the current line. A failed load keeps the fallback.
 */
export function createLazyCatchUpOverlay(fallback: CatchUpOverlay, load: () => Promise<CatchUpOverlay>): CatchUpOverlay {
  let styled: CatchUpOverlay | null = null
  let loading = false
  let text: string | null = null
  function start(): void {
    if (loading) return
    loading = true
    load()
      .then((o) => {
        styled = o
        if (text === null) return
        fallback.hide()
        o.show(text)
      })
      .catch((err: unknown) => logger.warn('strategic link: the styled catch-up overlay did not load; the plain line stays', err))
  }
  return {
    show(t: string): void {
      text = t
      if (styled !== null) {
        styled.show(t)
        return
      }
      start()
      fallback.show(t)
    },
    hide(): void {
      text = null
      styled?.hide()
      fallback.hide()
    },
  }
}
